import { File } from '@/shared/api/protocol-ts/model/dto_asset_common_pb';
import { Asset, AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';
import {
  Report,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Tooth } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { Report_2D_Image_Meta } from '@/shared/api/protocol-ts/model/dto_report_type_2D_common_pb';
import { getMasksData } from '@/shared/embeddedLibs/conditionsAndMasks';
import {
  buildPanoWingsMatrix,
  type ComparePaneInput,
  type MIRenderImageInterface,
  type MIRenderImageMatrix,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import {
  groupIOXRayImagesByPartition,
  type IOXrayImageInterface,
} from '@/entities/IOXRayImagesMatrix';

type ComparePaneSource = {
  report: Report;
  // Full-size images of the report as ListReportPanoramasWithConditions
  // returned them
  panoramas: Asset[];
  // 2D images of the report with the teeth they show, for the FMX layout
  imageInterfaces: IOXrayImageInterface[];
  conditions: Condition[];
  teeth: Tooth[];
  featureFlags: Partial<Record<FeatureFlag, boolean>>;
};

// The file the renderer loads for an image asset
const getImageFile = (asset: Asset): File | undefined => {
  if (asset.Kind.case === 'GeneratedReport') {
    const content = asset.Kind.value.Content;

    switch (content.case) {
      case 'CBCTGPPanoramaReformatGeneral':
        return content.value.PanoramaSingle;
      case 'CBCTEndoPanoramaSplit':
        return content.value.PanoramaSplit;
      case 'CBCTMolarPanorama':
        return content.value.Panorama;
      case 'IOXRayImage':
        return content.value.Image;
      default:
        return undefined;
    }
  }

  if (asset.Kind.case === 'Study') {
    const content = asset.Kind.value.Content;

    switch (content.case) {
      case 'PanoramicXRay':
        return content.value.PanoramaImage;
      case 'IntraoralXRay':
        return content.value.Image;
      default:
        return undefined;
    }
  }

  return undefined;
};

const get2DImagesMeta = (report: Report): Report_2D_Image_Meta[] => {
  switch (report.Specific.case) {
    case 'DataIOXRayGP':
    case 'DataPanoGP':
    case 'DataPanoBitewings':
      return report.Specific.value.ImagesMeta;
    default:
      return [];
  }
};

type ViewOptions = NonNullable<
  NonNullable<Report['MedicalImageFeatures']>['ViewOptions']
>;

const clamp = (value: number, limit: number) =>
  Math.min(limit, Math.max(-limit, value));

// The report page adds the report-level view options to every image on top
// of the image's own (its "screen props"); the compare view has no such
// layer, so the sum is handed over per image with the same clamps.
const getRenderViewOptions = (
  imageOptions?: ViewOptions,
  reportOptions?: ViewOptions,
) => ({
  Brightness: clamp(
    (imageOptions?.Brightness ?? 0) + (reportOptions?.Brightness ?? 0),
    1,
  ),
  Contrast: clamp(
    (imageOptions?.Contrast ?? 0) + (reportOptions?.Contrast ?? 0),
    1,
  ),
  Sharpness: clamp(
    (imageOptions?.Sharpness ?? 0) + (reportOptions?.Sharpness ?? 0),
    3,
  ),
});

// Same fields the report pages hand to the renderer: orientation and
// physical scale come from the report's image meta.
const toRenderImage = (
  asset: Asset,
  report: Report,
  imageMeta?: Report_2D_Image_Meta,
): MIRenderImageInterface => {
  const file = getImageFile(asset);
  const size =
    file?.Metadata.case === 'Image' ? file.Metadata.value : undefined;

  return {
    ImageID: asset.ID,
    OriginalFileID: file?.ID ?? '',
    OrientationAngle: imageMeta?.OrientationAngle ?? 0,
    MedicalImageFeatures: {
      ViewOptions: getRenderViewOptions(
        imageMeta?.MedicalImageFeatures?.ViewOptions,
        report.MedicalImageFeatures?.ViewOptions,
      ),
    },
    originalSize: {
      width: Number(size?.Width ?? 0),
      height: Number(size?.Height ?? 0),
    },
    pixelSpacing: imageMeta?.Scale && {
      xMmPerPixel: imageMeta.Scale.X,
      yMmPerPixel: imageMeta.Scale.Y,
    },
  };
};

const toRenderImages = (report: Report, images: IOXrayImageInterface[]) =>
  images.map((image) => toRenderImage(image.asset, report, image.imageMeta));

// Intraoral images in the FMX cells of the report page
const getIntraoralMatrix = (
  report: Report,
  images: IOXrayImageInterface[],
): MIRenderImageMatrix => {
  const grouped = groupIOXRayImagesByPartition(images);

  return [
    [grouped.UpperLeft, grouped.UpperMiddle, grouped.UpperRight],
    [grouped.MiddleLeft, grouped.MiddleMiddle, grouped.MiddleRight],
    [grouped.LowerLeft, grouped.LowerMiddle, grouped.LowerRight],
  ].map((row) => row.map((cell) => toRenderImages(report, cell)));
};

export const getComparePaneInput = ({
  report,
  panoramas,
  imageInterfaces,
  conditions,
  teeth,
  featureFlags,
}: ComparePaneSource): ComparePaneInput | null => {
  if (!panoramas.length) {
    return null;
  }

  const panoramaIDs = panoramas.map((asset) => asset.ID);
  // Same mask set as the report page: conditions, maxfax anatomy and tooth
  // anatomy with the report page colors; masks of other images are dropped
  // by the compare view.
  const masks = getMasksData({
    conditions,
    report,
    teeth,
    assets: panoramas,
    featureFlags,
  });
  const shownImages = imageInterfaces.filter((image) =>
    panoramaIDs.includes(image.asset.ID),
  );
  const intraoralImages = shownImages.filter(
    (image) => image.asset.Type !== AssetType.AssetType_Study_PanoramicXRay,
  );

  switch (report.Type) {
    case ReportType.ReportType_IOXRay_GP:
      return { matrix: getIntraoralMatrix(report, intraoralImages), masks };

    case ReportType.ReportType_Pano_Bitewings: {
      const pano = shownImages.find(
        (image) => image.asset.Type === AssetType.AssetType_Study_PanoramicXRay,
      );
      if (!pano) {
        return { matrix: getIntraoralMatrix(report, intraoralImages), masks };
      }
      // Same layout as the Pano + Bitewings report page: the bitewings row
      // sits under the panorama, which is scaled to the row width.
      const grouped = groupIOXRayImagesByPartition(intraoralImages);
      const bitewings = toRenderImages(report, [
        ...grouped.MiddleLeft,
        ...grouped.MiddleRight,
      ]);

      return {
        ...buildPanoWingsMatrix(
          toRenderImage(pano.asset, report, pano.imageMeta),
          bitewings,
        ),
        masks,
      };
    }

    default: {
      const [asset] = panoramas;
      const imageMeta = get2DImagesMeta(report).find(
        (meta) => (meta.GeneratedAssetID || meta.StudyAssetID) === asset.ID,
      );

      return { matrix: [[[toRenderImage(asset, report, imageMeta)]]], masks };
    }
  }
};
